import React, { useEffect } from 'react';
import css from './UnlockableContent.module.css';
import { FieldArray } from 'react-final-form-arrays';
import { Field } from 'react-final-form';
import { useIntl } from 'react-intl';
import FileUploadField from './FileUploadField';
import { requiredMediaFile, requiredPhoto } from '../../../../../util/validators';
import PriceInputField from './PriceInputField';
import ContentExpireSection from './ContentExpireSection';

const SectionFieldArray = ({ name, labelId, onSave, values, activeContentType }) => {
  const intl = useIntl();
  const label = intl.formatMessage({
    id: labelId,
    defaultMessage: labelId.replace('Label', ''),
  });

  const isPostSection = name === 'unlockableContent.posts';
  return (
    <FieldArray name={name}>
      {({ fields }) => {
        useEffect(() => {
          if (fields.length === 0) {
            fields.push({ links: [''] }); // Initialize with one empty link for posts
          }
        }, [fields]);

        return (
          <div className={css.sectionContent}>
            {fields.map((fieldName, index) => {
              const field = values.unlockableContent?.[activeContentType]?.[index] || {};

              return (
                <div key={fieldName} className={css.itemRow}>
                  {/* Common Title Field */}
                  <div className={css.itemFld}>
                    <label htmlFor={`${fieldName}.title`}>
                      {intl.formatMessage({
                        id: `${labelId}.titlePlaceholder`,
                        defaultMessage: `Enter ${label} title or filename`,
                      })}
                    </label>
                    <Field
                      name={`${fieldName}.title`}
                      component="input"
                      placeholder={intl.formatMessage({
                        id: `${labelId}.titlePlaceholder`,
                        defaultMessage: `Enter ${label} title or filename`,
                      })}
                      className={css.input}
                    />
                  </div>

                  {isPostSection ? (
                    <>
                      {/* Post Content */}
                      <div className={css.itemFld}>
                        <label htmlFor={`${fieldName}.content`}>
                          {intl.formatMessage({
                            id: 'UnlockableContent.postContentLabel',
                            defaultMessage: 'Post Content',
                          })}
                        </label>
                        <Field
                          name={`${fieldName}.content`}
                          component="textarea"
                          placeholder={intl.formatMessage({
                            id: 'UnlockableContent.postContentPlaceholder',
                            defaultMessage: 'Write your post content here...',
                          })}
                          className={css.textarea}
                        />
                      </div>

                      <PriceInputField fieldName={fieldName} labelId={labelId} />

                      <ContentExpireSection values={values} fieldName={fieldName} />
                      {/* Upload a Photo (Appended to Post) */}
                      <div className={css.itemFld}>
                        <label htmlFor={`${fieldName}.photo`}>
                          {intl.formatMessage({
                            id: 'UnlockableContent.uploadPhotoLabel',
                            defaultMessage: 'Upload a photo to append',
                          })}
                        </label>
                        <Field
                          name={`${fieldName}.photo`}
                          validate={requiredPhoto(
                            intl.formatMessage({
                              id: 'UnlockableContent.uploadPhotoRequired',
                              defaultMessage: 'Please upload a photo',
                            })
                          )}
                          component={({ meta, ...rest }) => (
                            <div>
                              <FileUploadField
                                {...rest}
                                values={values}
                                activeContentType={activeContentType}
                                onSave={onSave}
                                onChange={file => {
                                  // Update the field with the uploaded photo
                                  fields.update(index, { ...field, photo: file });
                                }}
                              />
                              {meta.error && (
                                <div className={css.error} role="alert">
                                  {meta.error}
                                </div>
                              )}
                            </div>
                          )}
                        />
                      </div>

                      {/* Multiple Links */}
                      <FieldArray name={`${fieldName}.links`}>
                        {({ fields: linkFields }) => (
                          <div className={css.itemFld}>
                            <label>
                              {intl.formatMessage({
                                id: 'UnlockableContent.linksLabel',
                                defaultMessage: 'Links (optional)',
                              })}
                            </label>
                            {linkFields.map((linkName, linkIndex) => (
                              <div key={linkName} className={css.linkRow}>
                                <Field
                                  name={linkName}
                                  component="input"
                                  type="url"
                                  placeholder={intl.formatMessage({
                                    id: 'UnlockableContent.linkPlaceholder',
                                    defaultMessage: 'https://yourlink.com',
                                  })}
                                  className={css.input}
                                />
                                <button
                                  type="button"
                                  onClick={() => linkFields.remove(linkIndex)}
                                  className={css.removeButton}
                                >
                                  {intl.formatMessage({
                                    id: 'UnlockableContent.removeLink',
                                    defaultMessage: 'Remove Link',
                                  })}
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => linkFields.push('')}
                              className={css.addButton}
                            >
                              {intl.formatMessage({
                                id: 'UnlockableContent.addLink',
                                defaultMessage: '+ Add Another Link',
                              })}
                            </button>
                          </div>
                        )}
                      </FieldArray>
                    </>
                  ) : (
                    <>
                      {/* Default flow (Price + File Upload) */}
                      <PriceInputField fieldName={fieldName} labelId={labelId} />
                      <ContentExpireSection values={values} fieldName={fieldName} />
                      <div className={css.itemFld}>
                        <label htmlFor={`${fieldName}.files`}>
                          {intl.formatMessage({
                            id: 'UnlockableContent.uploadFilesLabel',
                            defaultMessage: 'Upload files',
                          })}
                        </label>
                        <Field
                          name={`${fieldName}.files`}
                          validate={requiredMediaFile(
                            intl.formatMessage({
                              id: 'UnlockableContent.uploadMediaRequired',
                              defaultMessage: 'Please upload a media file',
                            })
                          )}
                          component={({ meta, ...rest }) => (
                            <div>
                              <FileUploadField
                                {...rest}
                                values={values}
                                activeContentType={activeContentType}
                                onSave={onSave}
                                onBlur={rest.onBlur}
                                onTouched={() => rest.onBlur && rest.onBlur()}
                              />
                              {meta.error && (
                                <div className={css.error} role="alert">
                                  {meta.error}
                                </div>
                              )}
                            </div>
                          )}
                        />
                      </div>
                    </>
                  )}

                  {/* Remove Post/Item Button */}
                  {fields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => fields.remove(index)}
                      className={css.removeButton}
                    >
                      {intl.formatMessage({
                        id: `${labelId}.remove`,
                        defaultMessage: `Remove ${label}`,
                      })}
                    </button>
                  )}
                </div>
              );
            })}

            {/* Add New Post/File Button */}
            <button
              type="button"
              onClick={() => fields.push({ links: [''] })}
              className={css.addButton}
            >
              {intl.formatMessage({
                id: `${labelId}.add`,
                defaultMessage: `+ Add New ${label}`,
              })}
            </button>
          </div>
        );
      }}
    </FieldArray>
  );
};

export default SectionFieldArray;
